import { useState, useEffect, useCallback } from 'react';

interface QuizOption {
  value: string;
  label: string;
  icon: string;
}

interface QuizStep {
  id: string;
  question: string;
  subtext?: string;
  options: QuizOption[];
}

const QUIZ_STEPS: QuizStep[] = [
  {
    id: 'goal',
    question: 'What is your primary goal?',
    subtext: 'We\'ll match you with the right coverage type',
    options: [
      { value: 'protect_income', label: 'Protect my family\'s income', icon: 'ri-heart-pulse-fill' },
      { value: 'mortgage', label: 'Pay off my mortgage if I pass', icon: 'ri-home-heart-fill' },
      { value: 'final_expense', label: 'Cover final expenses / burial', icon: 'ri-shield-user-fill' },
      { value: 'wealth', label: 'Build retirement wealth', icon: 'ri-funds-fill' },
    ],
  },
  {
    id: 'dependents',
    question: 'How many people depend on your income?',
    subtext: 'This helps us size the right coverage amount',
    options: [
      { value: 'just_me', label: 'Just me', icon: 'ri-user-3-fill' },
      { value: '1_2', label: '1–2 people', icon: 'ri-group-fill' },
      { value: '3_4', label: '3–4 people', icon: 'ri-team-fill' },
      { value: '5_plus', label: '5 or more', icon: 'ri-community-fill' },
    ],
  },
  {
    id: 'age',
    question: 'What\'s your age range?',
    subtext: 'Age is the biggest factor in your rate',
    options: [
      { value: '18_30', label: '18 – 30', icon: 'ri-seedling-fill' },
      { value: '31_45', label: '31 – 45', icon: 'ri-user-fill' },
      { value: '46_55', label: '46 – 55', icon: 'ri-user-star-fill' },
      { value: '56_plus', label: '56 or older', icon: 'ri-user-heart-fill' },
    ],
  },
  {
    id: 'budget',
    question: 'What\'s your monthly budget?',
    subtext: 'We always find options that fit your budget',
    options: [
      { value: 'under_30', label: 'Under $30 / mo', icon: 'ri-money-dollar-circle-line' },
      { value: '30_75', label: '$30 – $75 / mo', icon: 'ri-coins-fill' },
      { value: '75_150', label: '$75 – $150 / mo', icon: 'ri-bank-card-fill' },
      { value: '150_plus', label: '$150+ / mo', icon: 'ri-secure-payment-fill' },
    ],
  },
];

function getRecommendation(answers: Record<string, string>): { type: string; amount: string; rate: string; description: string } {
  const { goal, dependents, age, budget } = answers;

  if (goal === 'final_expense' || age === '56_plus') {
    return {
      type: 'Final Expense Insurance',
      amount: '$10,000 – $25,000',
      rate: '$25 – $60/mo',
      description: 'A whole life policy that covers funeral and final costs, guaranteed approval for most ages with no medical exam required.',
    };
  }

  if (goal === 'wealth' && (age === '31_45' || age === '46_55')) {
    return {
      type: 'Indexed Universal Life (IUL)',
      amount: '$250,000 – $750,000',
      rate: '$100 – $250/mo',
      description: 'Permanent life insurance tied to market index performance — grows tax-free cash value you can use in retirement.',
    };
  }

  if (goal === 'mortgage') {
    return {
      type: 'Mortgage Protection Insurance',
      amount: 'Match your mortgage',
      rate: '$30 – $80/mo',
      description: 'Pays off your home if you pass — your family keeps the house no matter what happens to you.',
    };
  }

  if (dependents === '3_4' || dependents === '5_plus' || budget === '30_75' || budget === 'under_30') {
    return {
      type: '20-Year Term Life Insurance',
      amount: '$500,000 – $750,000',
      rate: '$25 – $55/mo',
      description: 'Maximum protection at the lowest cost during your family\'s highest-risk years. Most popular choice for growing families.',
    };
  }

  return {
    type: '20-Year Term Life Insurance',
    amount: '$500,000 – $1,000,000',
    rate: '$30 – $75/mo',
    description: 'Straightforward, affordable protection that covers your family for 20 years — your peak earning and child-raising years.',
  };
}

export default function NeedsAssessmentPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showResult, setShowResult] = useState(false);

  // Lead form state
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    sessionStorage.setItem('quizPopupSeen', '1');
  }, []);

  useEffect(() => {
    // Only show once per session
    const seen = sessionStorage.getItem('quizPopupSeen');
    if (seen) return;

    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 30000); // 30 seconds

    return () => clearTimeout(timer);
  }, []);

  const handleAnswer = (stepId: string, value: string) => {
    const newAnswers = { ...answers, [stepId]: value };
    setAnswers(newAnswers);

    if (currentStep < QUIZ_STEPS.length - 1) {
      setTimeout(() => setCurrentStep(prev => prev + 1), 280);
    } else {
      setTimeout(() => setShowResult(true), 280);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const recommendation = getRecommendation(answers);
      const formBody = new URLSearchParams();
      formBody.append('name', name);
      formBody.append('phone', phone);
      formBody.append('email', email);
      formBody.append('sms_consent', smsConsent ? 'Yes' : 'No');
      formBody.append('quiz_goal', answers.goal || '');
      formBody.append('quiz_dependents', answers.dependents || '');
      formBody.append('quiz_age', answers.age || '');
      formBody.append('quiz_budget', answers.budget || '');
      formBody.append('recommended_product', recommendation.type);
      formBody.append('source', 'homepage_quiz_popup');

      await fetch('https://readdy.ai/api/form/d6sp88b4rjv84lqbqi80', {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      setSubmitted(true);
    } catch {
      setSubmitted(true); // Still show success even if network issue
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const recommendation = showResult ? getRecommendation(answers) : null;
  const progress = ((currentStep + (showResult ? 1 : 0)) / QUIZ_STEPS.length) * 100;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Life insurance needs assessment"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl w-full max-w-[460px] overflow-hidden animate-[fadeInScale_0.3s_ease-out]">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0F2A3F] to-[#1A3A52] px-6 pt-6 pb-5 relative">
          <button
            onClick={handleClose}
            aria-label="Close quiz"
            className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center bg-white/15 hover:bg-white/25 rounded-full transition-colors cursor-pointer border-none"
          >
            <i className="ri-close-line text-white text-[16px]"></i>
          </button>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 flex items-center justify-center bg-[#D97642] rounded-full">
              <i className="ri-questionnaire-fill text-white text-[15px]"></i>
            </div>
            <span className="text-white/80 text-[12px] font-semibold uppercase tracking-widest">Free Assessment</span>
          </div>
          <h2 className="text-[20px] font-bold text-white leading-tight">
            {showResult
              ? 'Your Personalized Recommendation'
              : 'Find Your Perfect Coverage in 60 Seconds'}
          </h2>
          {!showResult && (
            <p className="text-white/70 text-[13px] mt-1">
              Question {currentStep + 1} of {QUIZ_STEPS.length}
            </p>
          )}

          {/* Progress bar */}
          {!showResult && (
            <div className="mt-4 h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#D97642] rounded-full transition-all duration-500 ease-out"
                style={{ width: `${((currentStep) / QUIZ_STEPS.length) * 100}%` }}
              />
            </div>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {submitted ? (
            /* Success State */
            <div className="flex flex-col items-center text-center py-4">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mb-4">
                <i className="ri-check-double-line text-[32px] text-green-600"></i>
              </div>
              <h3 className="text-[20px] font-bold text-[#1A3A52] mb-2">You're All Set!</h3>
              <p className="text-[14px] text-[#4A5568] leading-[1.7] mb-5">
                We'll reach out within a few hours with your personalized {recommendation?.type} options — no pressure, just honest info.
              </p>
              <div className="w-full bg-[#FDF6F0] border border-[#D97642]/20 rounded-xl p-4 mb-5 text-left">
                <p className="text-[12px] font-semibold text-[#D97642] uppercase tracking-wide mb-1">Your Match</p>
                <p className="text-[16px] font-bold text-[#1A3A52]">{recommendation?.type}</p>
                <p className="text-[13px] text-[#4A5568] mt-0.5">{recommendation?.amount} · {recommendation?.rate}</p>
              </div>
              <button
                onClick={handleClose}
                className="w-full py-3 bg-[#D97642] text-white text-[14px] font-semibold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap"
              >
                Close &amp; Explore Site
              </button>
            </div>
          ) : showResult && recommendation ? (
            /* Result + Lead Form */
            <div>
              {/* Recommendation Card */}
              <div className="bg-gradient-to-r from-[#FDF6F0] to-[#FFF8F4] border border-[#D97642]/30 rounded-xl p-4 mb-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#D97642] rounded-full flex-shrink-0">
                    <i className="ri-shield-check-fill text-white text-[18px]"></i>
                  </div>
                  <div>
                    <p className="text-[11px] font-bold text-[#D97642] uppercase tracking-wide mb-0.5">Best Match for You</p>
                    <h3 className="text-[17px] font-bold text-[#1A3A52] leading-tight">{recommendation.type}</h3>
                    <p className="text-[13px] text-[#4A5568] mt-1 leading-[1.5]">{recommendation.description}</p>
                    <div className="flex items-center gap-3 mt-2.5">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-[#D97642]/30 rounded-full text-[12px] font-semibold text-[#1A3A52]">
                        <i className="ri-money-dollar-circle-line text-[#D97642]"></i>
                        {recommendation.rate}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-[#D97642]/30 rounded-full text-[12px] font-semibold text-[#1A3A52]">
                        <i className="ri-shield-line text-[#D97642]"></i>
                        {recommendation.amount}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Lead Form */}
              <p className="text-[13px] font-semibold text-[#1A3A52] mb-3">
                Enter your info to get your exact quote:
              </p>
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-3"
              >
                <input
                  type="text"
                  placeholder="Full Name"
                  required
                  name="name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-[#1A3A52] placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="tel"
                    placeholder="Phone"
                    required
                    name="phone"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-[#1A3A52] placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    required
                    name="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-[#1A3A52] placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                  />
                </div>

                {/* SMS Consent */}
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="sms_consent"
                    required
                    checked={smsConsent}
                    onChange={e => setSmsConsent(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#D97642] flex-shrink-0"
                  />
                  <span className="text-[11px] text-gray-500 leading-relaxed">
                    I agree to receive text/call follow-up from Hoosier Life Insurance. Reply STOP to opt out.{' '}
                    <a href="/privacy-policy" className="text-[#D97642] underline" onClick={e => e.stopPropagation()}>Privacy Policy</a>
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={isSubmitting || !smsConsent}
                  className="w-full py-3.5 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i> Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill"></i> Get My Free Quote
                    </>
                  )}
                </button>
              </form>
            </div>
          ) : (
            /* Quiz Questions */
            <div key={currentStep}>
              <p className="text-[17px] font-bold text-[#1A3A52] mb-1 leading-snug">
                {QUIZ_STEPS[currentStep].question}
              </p>
              {QUIZ_STEPS[currentStep].subtext && (
                <p className="text-[13px] text-[#718096] mb-4">{QUIZ_STEPS[currentStep].subtext}</p>
              )}
              <div className="grid grid-cols-2 gap-2.5">
                {QUIZ_STEPS[currentStep].options.map((option) => {
                  const isSelected = answers[QUIZ_STEPS[currentStep].id] === option.value;
                  return (
                    <button
                      key={option.value}
                      onClick={() => handleAnswer(QUIZ_STEPS[currentStep].id, option.value)}
                      className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 text-left transition-all cursor-pointer hover:border-[#D97642] hover:bg-[#FDF6F0] group ${
                        isSelected
                          ? 'border-[#D97642] bg-[#FDF6F0]'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-colors ${
                        isSelected ? 'bg-[#D97642]' : 'bg-gray-100 group-hover:bg-[#D97642]/20'
                      }`}>
                        <i className={`${option.icon} text-[15px] ${isSelected ? 'text-white' : 'text-gray-600 group-hover:text-[#D97642]'}`}></i>
                      </div>
                      <span className={`text-[13px] font-semibold leading-tight ${
                        isSelected ? 'text-[#D97642]' : 'text-[#1A3A52]'
                      }`}>
                        {option.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer hint */}
        {!showResult && !submitted && (
          <div className="px-6 pb-4 text-center">
            <p className="text-[11px] text-gray-400">
              No spam. Your info is never shared or sold.
            </p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.92) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
